<script setup lang="ts">
import type {Ref} from "vue";
import {computed, onBeforeUnmount, ref, watch} from "vue";
import {useStore} from "vuex";
import isUndefined from "lodash/isUndefined";
import isEmpty from "lodash/isEmpty";
import ValidationMessage from "@/components/UI/ValidationMessage.vue";
import Length from "../../../source/js/shared/models/Geometry/Length";
import { endsWithSeparator, limitDecimals, stripNonNumeric, toDecimalPoint } from "@/functions/numbers";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import EditableItem from "@/components/EditableItem.vue";
import CustomDrillHole from "@/Models/CustomDrillHole";
import InputGroup from "@/components/UI/InputGroup.vue";
import MessageContainer from "@/components/UI/MessageContainer.vue";

const kpsSettings = window.measurementSelectorVariables;

const activeHole:Ref<CustomDrillHole> = ref({
	x: 0,
	y: 0,
	diameter: 0,
});

const activeIndex: Ref<number|null> = ref(0);
const isEditorVisible = ref(true);
const isRemoveButtonActive = ref(false);
const isEditingExistingHole = ref(false);
const listHasHoles = ref(false);

const store = useStore();

const customDrillHoles = computed(() => store.getters.customDrillHoles );
const editableCustomDrillHoles = computed(() => customDrillHoles.value.filter( ( value: CustomDrillHole, index: number ) => index !== activeIndex.value ) );
const drillHoleDiameter = computed(() => store.getters.drillHoleDiameter.in_cm() );
const isDrillHoleValid = computed(() => store.getters.isDrillHoleValid );
const getDrillHoleValidationMessage = computed(() => store.getters.getDrillHoleValidationMessage );
const minimumDrillHoleDiameterLabel = computed(() => store.getters.minimumDrillHoleDiameterLabel );
const activeIndexPlusOne = computed(() => activeIndex.value + 1);

const x = computed({
	get: () => activeHole.value.x ? new Length( activeHole.value.x).in_cm(): activeHole.value.x,
	set: (value: string|number) => {
		if(!endsWithSeparator(value)) {
			activeHole.value.x = Length.from_cm(limitDecimals(stripNonNumeric(value), 1)).in_mm();
		}
	}
});
const y = computed({
	get: () => activeHole.value.y ? new Length( activeHole.value["y"] ).in_cm() : activeHole.value.y,
	set: (value: string|number) => {
		if(!endsWithSeparator(value)) {
			activeHole.value.y = Length.from_cm(limitDecimals(stripNonNumeric(value), 1)).in_mm();
		}
	}
});
const diameter = computed({
	get: () => activeHole.value.diameter ? new Length( activeHole.value["diameter"] ).in_cm() : activeHole.value.diameter,
	set: (value: string|number) => {
		if(!endsWithSeparator(value)) {
			activeHole.value.diameter = Length.from_cm(limitDecimals(stripNonNumeric(value), 1)).in_mm();
		}
	}
});
const getCenter = computed(() => store.getters.getCenter).value;

function created(): void {
	document.addEventListener("focusin", ( event ) =>
		store.commit("updateFocusedElement", event )
	);

	const { drillHoleSettings } = kpsSettings;

	let drillHoles = drillHoleSettings.prefilledDrillHoles;

	if (store.getters.lastGeneratedConfiguration.drillholes?.settings) {
		drillHoles = store.getters.lastGeneratedConfiguration.drillholes?.settings;
	}

	if (! isEmpty(drillHoles)) {

		// If we don't set these properties, then we don't have access to the custom drill holes in the sidebar.
		isEditorVisible.value = false;
		activeIndex.value = null;

		setRemoveButtonActiveState();

		return;
	}

	addHole();

	activeHole.value = customDrillHoles.value[activeIndex.value];
}

function getI18nNumber(value: number): string {
	return new Length(value).in_cm_i18n();
}

function removeHole(index: number): void {
	store.commit("removeCustomHole", index);
	activeIndex.value = null;

	setRemoveButtonActiveState();
}

function addHole(): void {
	const center = getCenter(true, 'mm');

	store.commit("addCustomHole", {
		x: center.x,
		y: center.y,
		diameter: Length.from_cm( drillHoleDiameter.value ).in_mm()
	});
}

function editHole( holeNumber: number ): void {
	isEditorVisible.value = true;
	isEditingExistingHole.value = true;
	activeIndex.value = holeNumber;
}

function saveHole(): void {
	setRemoveButtonActiveState();

	isEditorVisible.value = false;
	activeIndex.value = null;
}

function addNewHole(): void {
	isEditorVisible.value = true;
	isEditingExistingHole.value = false;
	activeIndex.value = customDrillHoles.value.length;

	addHole();
}

function setRemoveButtonActiveState(): void {
	isRemoveButtonActive.value = customDrillHoles.value.length > 1;
}

function setListHasHoles(index: null|number): boolean {

	// if no hole is being edited check customDrillHoles length
	if (index < 0 || index === null) {
		return customDrillHoles.value.length > 0;
	}

	// if a hole is being edited remove active hole from customDrillHoles and check length
	const holes = [...customDrillHoles.value]; // clone customDrillHoles
	holes.splice(index, 1);

	return holes.length > 0;
}

watch(activeIndex, async(index: null|number): Promise<void> => {
	activeHole.value = index !== null ? customDrillHoles.value[index] : {};
	listHasHoles.value = setListHasHoles(index);

	store.commit("updateCurrentCustomDrillhole", index);
} );

watch(activeHole, async (newValue: CustomDrillHole, oldValue: CustomDrillHole): Promise<void> => {
	for (const key in newValue) {
		if (! isUndefined(oldValue[key])) {
			store.commit("updateUsingDefaultDrillHoleDimensions", false);
		}

		customDrillHoles.value[ activeIndex.value ][ key ] =
			typeof newValue[key] === "string"
				? toDecimalPoint( newValue[ key ] )
				: newValue[key];
	}
} );

onBeforeUnmount(() => {
	document.removeEventListener("focusin", () =>
		store.commit("updateFocusedElement")
	);

	store.commit("resetCustomHoles");
	store.commit("forceRefresh");
});

created();

</script>

<template>
	<div class="input-container">
		<ul class="editable-list" v-if="listHasHoles">
			<li
				class="editable-list__item"
				v-for="(hole, index) in editableCustomDrillHoles"
				:key="`hole-${index}`"
			>
				<EditableItem
					:edit="{ index, title: $i18n.drillHoleConfiguration.edit_hole, disabled: isEditorVisible, onClick: editHole, hidden: false, }"
					:remove="{ index, title: $i18n.drillHoleConfiguration.remove_hole, disabled: !isRemoveButtonActive, onClick: removeHole, hidden: false }"
				>

					{{ $i18n.drillHoleConfiguration.singular_hole }} {{ index + 1 }}
					<strong>
						X - {{ getI18nNumber( hole.x ) }} |
						Y - {{ getI18nNumber( hole.y ) }} |
						{{ $i18n.drillHoleConfiguration.diameter_drill_hole_short }} - {{ getI18nNumber( hole.diameter ) }}
					</strong>
				</EditableItem>
			</li>
		</ul>
		<div v-if="isEditorVisible">
			<div class="shape-inputs">
				<dimension-input
					inputRef="drillHoleOffsetX"
					id="drillHoleOffsetX"
					name="drillHoleOffsetX"
					unit="cm"
					:label="$i18n.drillHoleConfiguration.distance_from_edge_left"
					:invalid="!isDrillHoleValid('drillHoleOffsetX', activeIndexPlusOne)"
					v-model="x"
				/>

				<dimension-input
					inputRef="drillHoleOffsetY"
					id="drillHoleOffsetY"
					name="drillHoleOffsetY"
					unit="cm"
					:label="$i18n.drillHoleConfiguration.distance_from_edge_bottom"
					:invalid="!isDrillHoleValid('drillHoleOffsetY', activeIndexPlusOne)"
					v-model="y"
				/>

				<dimension-input
					inputRef="drillHoleDiameter"
					id="drillHoleDiameter"
					name="drillHoleDiameter"
					unit="cm"
					:label="`${$i18n.drillHoleConfiguration.diameter_drill_hole} (${minimumDrillHoleDiameterLabel})`"
					:invalid="!isDrillHoleValid('drillHoleDiameter', activeIndexPlusOne)"
					v-model="diameter"
				/>

				<input-group flex-justify="bottom">
					<button type="button" v-on:click="saveHole();" class="measurements-selector__button-add">
						{{ isEditingExistingHole ? $i18n.drillHoleConfiguration.edit_hole : $i18n.drillHoleConfiguration.add_hole }}
					</button>
				</input-group>
			</div>

			<message-container>
				<!-- Message for drillHoleDiameter -->
				<validation-message
					v-if="!isDrillHoleValid(`drillHoleDiameter_${activeIndexPlusOne}`)"
					input-id="drillHoleDiameter"
				>
					{{ getDrillHoleValidationMessage(`drillHoleDiameter_${activeIndexPlusOne}`) }}
				</validation-message>

				<validation-message
					v-if="!isDrillHoleValid(`drillHoleOffsetX`, activeIndexPlusOne)"
					input-id="drillHoleOffsetX"
				>
					{{ getDrillHoleValidationMessage(`drillHoleOffsetX`, activeIndexPlusOne) }}
				</validation-message>

				<validation-message
					v-if="!isDrillHoleValid(`drillHoleOffsetY`, activeIndexPlusOne)"
					input-id="drillHoleOffsetY"
				>
					{{ getDrillHoleValidationMessage(`drillHoleOffsetY`, activeIndexPlusOne) }}
				</validation-message>
			</message-container>
		</div>

		<input-group v-if="!isEditorVisible" flex-justify="bottom">
			<button type="button" v-on:click="addNewHole();" class="measurements-selector__button-add">
				{{ $i18n.drillHoleConfiguration.add_hole_long }}
			</button>
		</input-group>

		<validation-message
			v-if="! isDrillHoleValid(`drillHoleUnique_${activeIndexPlusOne}`)"
		>
			{{ getDrillHoleValidationMessage(`drillHoleUnique_${activeIndexPlusOne}`) }}
		</validation-message>

		<validation-message
			v-if="! isDrillHoleValid(`drillHoleMissing_${activeIndexPlusOne}`)"
		>
			{{ getDrillHoleValidationMessage(`drillHoleMissing_${activeIndexPlusOne}`) }}
		</validation-message>
	</div>
</template>

<style scoped lang="scss">
.input-container {
	background-color: $brand-blue-background;
	border-radius: 8px;
	padding: 20px;
	margin-top: 20px;

	display: flex;
	flex-wrap: wrap;
	gap: 10px;
	grid-column: 1/-1;
	justify-content: space-between;
	min-width: 0;
}

.shape-inputs {
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 12px;
}
</style>
